import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './FarmingMinigame.module.css';
import { ItemData } from '../data/ItemData';
import StatusDisplay from './Layout/StatusDisplay';
import CollapsibleInventoryPanel from './Layout/CollapsibleInventoryPanel';

const FARMING_PHASES = {
  IDLE: 'idle',
  STARTING: 'starting',
  PLAYING: 'playing',
  RESULT: 'result',
};

const CROP_STAGES = {
  SEED: 'seed',
  SPROUT: 'sprout',
  GROWN: 'grown',
  WITHERED: 'withered'
};

const TILE_COLORS = {
  GRASS: '#4a7c42',
  TILLED: '#8b5e34',
  WATERED_TILLED: '#654321'
};

// Removed hardcoded mapping - now using actual ItemData for crop images

// Mapping from seed types to their corresponding harvested crop items
const SEED_TO_CROP_ITEM_MAP = {
  'carrotSeed': 'carrot',
  'cucumberSeed': 'cucumber',
  'eggplantSeed': 'eggplant',
  'garlicSeed': 'garlic',
  'cauliflowerSeed': 'cauliflower',
  'greenbeanSeed': 'greenbean',
  'kaleSeed': 'kale',
  'onionSeed': 'onion',
  'pepperSeed': 'pepper',
  'beetSeed': 'beet',
  'pumpkinSeed': 'pumpkin',
  'tomatoSeed': 'tomato',
};

// Tool icons mapping
const TOOL_ICONS = {
  'hoe': '/img/farming/hoe.png',
  'watering-can': '/img/farming/watering_can.png',
  'scythe': '/img/farming/scythe.png',
  'shovel': '/img/farming/shovel.png',
  'plant': '/img/farming/healthy_plant.png'
};

const FarmingMinigame = ({ playerData, onGameEnd, onEnergyUpdate, worldState }) => {
  const [phase, setPhase] = useState(FARMING_PHASES.IDLE);
  const [message, setMessage] = useState('Welcome to your farm!');
  const [currentTool, setCurrentTool] = useState('hoe');
  const [selectedSeed, setSelectedSeed] = useState(null);
  const [harvestedCrops, setHarvestedCrops] = useState(0);
  const [farmGrid, setFarmGrid] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [gridInitialized, setGridInitialized] = useState(false);
  const [cropsUpdated, setCropsUpdated] = useState(false);
  
  const canvasRef = useRef(null);
  const phaseRef = useRef(FARMING_PHASES.IDLE);
  const imagesRef = useRef({});
  const lastDrawTime = useRef(0);
  
  // Game settings - Changed to 4x4 grid with larger tiles
  const gameSettings = {
    gridWidth: 4,
    gridHeight: 4,
    tileSize: 80, // Increased from 60 to 80 for larger grid
    growthTimeHours: 72, // Default fallback (72 hours)
  };

  // Helper function to get crop item from seed type
  const getCropItemFromSeed = useCallback((seedType) => {
    return SEED_TO_CROP_ITEM_MAP[seedType] || null;
  }, []);

  // Helper function to get crop image path from seed type
  const getCropImagePath = useCallback((seedType) => {
    // Get the corresponding crop item from the seed type
    const cropItem = getCropItemFromSeed(seedType);
    if (cropItem && ItemData[cropItem] && ItemData[cropItem].img) {
      return ItemData[cropItem].img;
    }
    return 'farming/healthy_plant.png';
  }, [getCropItemFromSeed]);

  // Helper function to convert game time to comparable units
  const getGameTimeInMinutes = useCallback(() => {
    if (!worldState) return 0;
    return (worldState.day - 1) * 1440 + worldState.currentTimeInMinutes; // 1440 minutes per day
  }, [worldState]);

  // Get available seeds from player inventory
  const getAvailableSeeds = useCallback(() => {
    if (!playerData.inventory) return [];
    
    return Object.entries(playerData.inventory)
      .filter(([itemKey, quantity]) => {
        const item = ItemData[itemKey];
        return item && item.category === 'seed' && quantity > 0;
      })
      .map(([itemKey, quantity]) => ({
        key: itemKey,
        item: ItemData[itemKey],
        quantity: quantity
      }));
  }, [playerData.inventory]);

  // Update phase ref
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Preload images
  useEffect(() => {
    const preloadImages = () => {
      const imagesToLoad = [
        '/img/farming/healthy_plant.png',
        '/img/farming/wilted_plant.png',
        '/img/farming/grass_texture.png',
        '/img/farming/dirt_texture.png'
      ];
      
      // Add seed images to preload list
      Object.values(ItemData).forEach(item => {
        if (item.category === 'seed' && item.img) {
          imagesToLoad.push(`/img/${item.img}`);
        }
      });
      
      // Add crop images to preload list - use actual ItemData instead of hardcoded mapping
      Object.values(ItemData).forEach(item => {
        if (item.category === 'crop' && item.img) {
          imagesToLoad.push(`/img/${item.img}`);
        }
      });
      
      // Add tool icons to preload list
      Object.values(TOOL_ICONS).forEach(toolIconPath => {
        imagesToLoad.push(toolIconPath);
      });
      
      let loadedCount = 0;
      const totalImages = imagesToLoad.length;
      
      imagesToLoad.forEach(imagePath => {
        const img = new Image();
        img.onload = () => {
          imagesRef.current[imagePath] = img;
          loadedCount++;
          
          // Set images loaded when all images are loaded
          if (loadedCount === totalImages) {
            setImagesLoaded(true);
          }
        };
        img.onerror = () => {
          console.error(`Failed to load image: ${imagePath}`);
          loadedCount++;
          
          // Still mark as loaded even if some images fail
          if (loadedCount === totalImages) {
            setImagesLoaded(true);
          }
        };
        img.src = imagePath;
      });
    };
    
    preloadImages();
  }, []);

  // Initialize grid
  const initializeGrid = useCallback(() => {
    // Try to load existing farm state from playerData
    if (playerData.farmState && playerData.farmState.grid) {
      // Convert flattened grid back to 2D array if needed
      let loadedGrid = playerData.farmState.grid;
      if (Array.isArray(loadedGrid) && !Array.isArray(loadedGrid[0])) {
        // Convert flat array back to 2D
        loadedGrid = [];
        for (let y = 0; y < gameSettings.gridHeight; y++) {
          const row = [];
          for (let x = 0; x < gameSettings.gridWidth; x++) {
            const index = y * gameSettings.gridWidth + x;
            row.push(playerData.farmState.grid[index] || {
              tilled: false,
              watered: false,
              crop: null,
              plantedAt: null,
              lastWateredAt: null,
              seedType: null,
            });
          }
          loadedGrid.push(row);
        }
      }
      
      setFarmGrid(loadedGrid);
      setHarvestedCrops(playerData.farmState.harvestedCrops || 0);
      setGridInitialized(true);
      return;
    }
    
    // Create new grid if no existing state
    const grid = [];
    for (let y = 0; y < gameSettings.gridHeight; y++) {
      const row = [];
      for (let x = 0; x < gameSettings.gridWidth; x++) {
        row.push({
          tilled: false,
          watered: false,
          crop: null,
          plantedAt: null, // Store when the crop was planted
          lastWateredAt: null, // Store when last watered
          seedType: null, // Store what type of seed was planted
        });
      }
      grid.push(row);
    }
    setFarmGrid(grid);
    setGridInitialized(true);
  }, [gameSettings.gridHeight, gameSettings.gridWidth, playerData.farmState]);

  // Update crops based on game time
  const updateCropsBasedOnTime = useCallback((grid) => {
    const currentGameTime = getGameTimeInMinutes();
    let hasChanges = false;
    
    const updatedGrid = grid.map(row => 
      row.map(tile => {
        if (tile.crop && tile.plantedAt && tile.seedType) {
          const seedData = ItemData[tile.seedType];
          const growthTime = seedData?.growTime || gameSettings.growthTimeHours;
          const growthTimeInMinutes = growthTime * 60; // Convert hours to minutes
          const minutesElapsed = currentGameTime - tile.plantedAt;
          
          // Skip watering requirements for grown crops - they just wait for harvest
          if (tile.crop.stage === CROP_STAGES.GROWN) {
            return tile; // Grown crops don't need updates
          }
          
          // Skip withered crops - they stay withered
          if (tile.crop.stage === CROP_STAGES.WITHERED) {
            return tile;
          }
          
          // Calculate minutes since last watered or planted (whichever is more recent)
          const lastSafeTime = tile.lastWateredAt || tile.plantedAt;
          const minutesSinceLastSafe = currentGameTime - lastSafeTime;
          
          // Watering logic: 24hr safe period, then 24hr danger period, then wither
          // 0-1440 minutes: safe
          // 1440-2880 minutes: needs water but still alive
          // 2880+ minutes: withers
          
          // Check if crop should wither (only after 48 hours without water)
          if (minutesSinceLastSafe > 2880) {
            hasChanges = true;
            return {
              ...tile,
              crop: { ...tile.crop, stage: CROP_STAGES.WITHERED },
              watered: false,
            };
          }
          
          // Check if crop should grow to full size
          if (minutesElapsed >= growthTimeInMinutes) {
            // Crop is ready to be grown - check if it's not withered
            hasChanges = true;
            return {
              ...tile,
              crop: { ...tile.crop, stage: CROP_STAGES.GROWN },
              watered: false, // Reset watered state since grown crops don't need water
            };
          }
          
          // Check for sprout stage
          if (minutesElapsed >= growthTimeInMinutes / 2 && tile.crop.stage === CROP_STAGES.SEED) {
            hasChanges = true;
            return {
              ...tile,
              crop: { ...tile.crop, stage: CROP_STAGES.SPROUT }
            };
          }
        }
        return tile;
      })
    );
    
    return { updatedGrid, hasChanges };
  }, [getGameTimeInMinutes, gameSettings.growthTimeHours]);

  // Save farm state to player data
  const saveFarmState = useCallback((grid, harvested) => {
    // Flatten 2D grid to 1D array for Firebase compatibility
    const flattenedGrid = [];
    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[y].length; x++) {
        flattenedGrid.push(grid[y][x]);
      }
    }
    
    const farmState = {
      grid: flattenedGrid,
      harvestedCrops: harvested,
      lastUpdated: getGameTimeInMinutes()
    };
    
    // Use setTimeout to avoid setState during render error
    if (onEnergyUpdate) {
      setTimeout(() => {
        onEnergyUpdate({ 
          farmState: farmState
        });
      }, 0);
    }
  }, [onEnergyUpdate, getGameTimeInMinutes]);

  // Handle canvas click
  const handleCanvasClick = useCallback((event) => {
    if (phase !== FARMING_PHASES.PLAYING) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const gridX = Math.floor(mouseX / gameSettings.tileSize);
    const gridY = Math.floor(mouseY / gameSettings.tileSize);

    if (gridX < 0 || gridX >= gameSettings.gridWidth || gridY < 0 || gridY >= gameSettings.gridHeight) return;

    const newGrid = [...farmGrid];
    const tile = newGrid[gridY][gridX];
    const currentGameTime = getGameTimeInMinutes();

    switch (currentTool) {
      case 'hoe':
        if (!tile.tilled) {
          // Check if player has enough energy
          if (playerData.energy.current < 5) {
            setMessage('Not enough energy to till soil! (5 energy required)');
            return;
          }
          
          tile.tilled = true;
          setMessage('Soil tilled! Now you can plant seeds.');
          
          // Deduct energy
          if (onEnergyUpdate) {
            onEnergyUpdate({ energyChange: -5 });
          }
        } else {
          setMessage('This soil is already tilled!');
        }
        break;
        
      case 'plant':
        if (!selectedSeed) {
          setMessage('Please select a seed first!');
          return;
        }
        
        if (tile.tilled && !tile.crop) {
          // Check if player has the seed in inventory
          const availableSeeds = getAvailableSeeds();
          const seedInInventory = availableSeeds.find(s => s.key === selectedSeed);
          
          if (!seedInInventory || seedInInventory.quantity <= 0) {
            setMessage('You don\'t have this seed in your inventory!');
            return;
          }
          
          tile.crop = { stage: CROP_STAGES.SEED };
          tile.plantedAt = currentGameTime;
          tile.lastWateredAt = null; // Seeds don't start watered
          tile.watered = false;
          tile.seedType = selectedSeed; // Store the seed type
          
          const seedData = ItemData[selectedSeed];
          setMessage(`${seedData.name} planted! Remember to water it to help it grow.`);
          
          // Remove seed from inventory
          if (onEnergyUpdate) {
            onEnergyUpdate({ 
              inventoryChange: { [selectedSeed]: -1 }
            });
          }
        } else if (!tile.tilled) {
          setMessage('You need to till the soil first!');
        } else {
          setMessage('There is already a crop planted here!');
        }
        break;
        
      case 'watering-can':
        if (tile.tilled && tile.crop) {
          // Check if crop is already fully grown
          if (tile.crop.stage === CROP_STAGES.GROWN) {
            setMessage('This crop is fully grown and doesn\'t need watering! Use the scythe to harvest it.');
            return;
          }
          
          // Check if crop is withered
          if (tile.crop.stage === CROP_STAGES.WITHERED) {
            setMessage('This crop has withered and cannot be watered. Use the scythe to clear it.');
            return;
          }
          
          // Check if player has enough energy
          if (playerData.energy.current < 5) {
            setMessage('Not enough energy to water crops! (5 energy required)');
            return;
          }
          
          // Check crop watering status with new 48-hour system
          const lastSafeTime = tile.lastWateredAt || tile.plantedAt;
          const minutesSinceLastSafe = currentGameTime - lastSafeTime;
          
          if (minutesSinceLastSafe <= 1440) {
            // Still in safe period
            if (tile.lastWateredAt) {
              setMessage('This crop was recently watered and doesn\'t need water yet!');
            } else {
              setMessage('This newly planted seed doesn\'t need water yet (24-hour grace period)!');
            }
            return;
          }
          
          // Crop needs water (it's been 24+ hours)
          tile.watered = true;
          tile.lastWateredAt = currentGameTime;
          
          if (minutesSinceLastSafe > 2160) { // 36 hours - getting close to danger
            setMessage('Crop watered just in time! It was getting close to withering.');
          } else if (minutesSinceLastSafe > 1440) { // 24-48 hours - needs water
            setMessage('Crop watered! It was getting thirsty.');
          } else {
            setMessage('Crop watered! Plants grow faster when watered.');
          }
          
          // Deduct energy
          if (onEnergyUpdate) {
            onEnergyUpdate({ energyChange: -5 });
          }
        } else if (!tile.tilled) {
          setMessage('You need to till the soil first!');
        } else if (!tile.crop) {
          setMessage('There is no crop to water here!');
        }
        break;
        
      case 'shovel':
        if (tile.crop && tile.crop.stage === CROP_STAGES.SEED) {
          // Check if player has enough energy
          if (playerData.energy.current < 5) {
            setMessage('Not enough energy to dig up seeds! (5 energy required)');
            return;
          }
          
          const seedType = tile.seedType;
          
          // Remove crop but keep tilled state
          tile.crop = null;
          tile.watered = false;
          tile.plantedAt = null;
          tile.lastWateredAt = null;
          tile.seedType = null;
          
          // Return seed to inventory
          if (seedType && ItemData[seedType]) {
            setMessage(`${ItemData[seedType].name} dug up and returned to inventory!`);
            
            // Add seed back to inventory and deduct energy
            if (onEnergyUpdate) {
              onEnergyUpdate({ 
                energyChange: -5,
                inventoryChange: { [seedType]: 1 }
              });
            }
          } else {
            setMessage('Seed dug up!');
            
            // Just deduct energy
            if (onEnergyUpdate) {
              onEnergyUpdate({ energyChange: -5 });
            }
          }
        } else if (!tile.crop) {
          setMessage('There is no seed to dig up here!');
        } else {
          setMessage('Can only dig up seeds, not grown crops! Use scythe to harvest.');
        }
        break;
        
      case 'scythe':
        if (tile.crop && (tile.crop.stage === CROP_STAGES.GROWN || tile.crop.stage === CROP_STAGES.WITHERED)) {
          const wasGrown = tile.crop.stage === CROP_STAGES.GROWN;
          const seedType = tile.seedType;
          
          tile.crop = null;
          tile.tilled = false;
          tile.watered = false;
          tile.plantedAt = null;
          tile.lastWateredAt = null;
          tile.seedType = null;
          
          if (wasGrown) {
            const newHarvested = harvestedCrops + 1;
            setHarvestedCrops(newHarvested);
            
            // Get the harvested crop item
            const cropItem = getCropItemFromSeed(seedType);
            
            if (cropItem && ItemData[cropItem]) {
              setMessage(`${ItemData[cropItem].name} harvested! Added to inventory!`);
              
              // Add harvested crop to inventory
              if (onEnergyUpdate) {
                onEnergyUpdate({
                  inventoryChange: { [cropItem]: 1 }
                });
              }
            } else if (seedType && ItemData[seedType]) {
              setMessage(`${ItemData[seedType].name} harvested! Great work!`);
            } else {
              setMessage('Crop harvested! Great work!');
            }
            
            // Save state after harvest
            saveFarmState(newGrid, newHarvested);
          } else {
            setMessage('Withered crop cleared.');
          }
        } else if (!tile.crop) {
          setMessage('There is no crop to harvest here!');
        } else {
          setMessage('This crop is not ready for harvest yet!');
        }
        break;
    }

    setFarmGrid(newGrid);
    saveFarmState(newGrid, harvestedCrops);
  }, [phase, farmGrid, currentTool, selectedSeed, gameSettings, playerData.energy.current, onEnergyUpdate, harvestedCrops, saveFarmState, getAvailableSeeds, getCropItemFromSeed]);

  // Draw grid on canvas - optimized to reduce blinking with double buffering
  const drawGrid = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imagesLoaded) {
      return;
    }
    
    // Reduce throttling for smoother interaction
    const now = Date.now();
    if (now - lastDrawTime.current < 16) { // ~60 FPS instead of 200ms
      return;
    }
    lastDrawTime.current = now;
    
    const ctx = canvas.getContext('2d');
    
    // Create off-screen canvas for double buffering to eliminate flicker
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = canvas.width;
    offscreenCanvas.height = canvas.height;
    const offscreenCtx = offscreenCanvas.getContext('2d');
    
    // Draw to off-screen canvas first
    
    // Use current farmGrid without updating it during draw
    const currentGameTime = getGameTimeInMinutes();
    
    for (let y = 0; y < gameSettings.gridHeight; y++) {
      for (let x = 0; x < gameSettings.gridWidth; x++) {
        const tile = farmGrid[y] && farmGrid[y][x];
        if (!tile) continue;
        
        // Draw tile background using textures
        const tileX = x * gameSettings.tileSize;
        const tileY = y * gameSettings.tileSize;
        
        if (tile.tilled) {
          // Use dirt texture for tilled soil
          if (imagesRef.current['/img/farming/dirt_texture.png'] && imagesRef.current['/img/farming/dirt_texture.png'].complete) {
            offscreenCtx.drawImage(imagesRef.current['/img/farming/dirt_texture.png'], tileX, tileY, gameSettings.tileSize, gameSettings.tileSize);
          } else {
            // Fallback to solid color if texture not loaded
            offscreenCtx.fillStyle = TILE_COLORS.TILLED;
            offscreenCtx.fillRect(tileX, tileY, gameSettings.tileSize, gameSettings.tileSize);
          }
          
          // Add darker tint if watered
          if (tile.watered) {
            offscreenCtx.fillStyle = 'rgba(0, 0, 0, 0.2)';
            offscreenCtx.fillRect(tileX, tileY, gameSettings.tileSize, gameSettings.tileSize);
          }
        } else {
          // Use grass texture for untilled soil
          if (imagesRef.current['/img/farming/grass_texture.png'] && imagesRef.current['/img/farming/grass_texture.png'].complete) {
            offscreenCtx.drawImage(imagesRef.current['/img/farming/grass_texture.png'], tileX, tileY, gameSettings.tileSize, gameSettings.tileSize);
          } else {
            // Fallback to solid color if texture not loaded
            offscreenCtx.fillStyle = TILE_COLORS.GRASS;
            offscreenCtx.fillRect(tileX, tileY, gameSettings.tileSize, gameSettings.tileSize);
          }
        }

        // Draw grid lines - Made more visible for larger tiles
        offscreenCtx.strokeStyle = '#2a4c22';
        offscreenCtx.lineWidth = 2; // Thicker lines for larger tiles
        offscreenCtx.strokeRect(tileX, tileY, gameSettings.tileSize, gameSettings.tileSize);

        // Draw crop
        if (tile.crop) {
          const cropSize = gameSettings.tileSize * 0.8;
          const cropX = tileX + (gameSettings.tileSize - cropSize) / 2;
          const cropY = tileY + (gameSettings.tileSize - cropSize) / 2;
          
          let imagePath = '';
          switch (tile.crop.stage) {
            case CROP_STAGES.SEED:
              // Draw actual seed image if available
              if (tile.seedType && ItemData[tile.seedType]) {
                imagePath = `/img/${ItemData[tile.seedType].img}`;
              } else {
                // Fallback to brown dot if no seed image
                offscreenCtx.fillStyle = '#8B4513';
                offscreenCtx.beginPath();
                offscreenCtx.arc(tileX + gameSettings.tileSize / 2, tileY + gameSettings.tileSize / 2, 4, 0, 2 * Math.PI);
                offscreenCtx.fill();
              }
              break;
            case CROP_STAGES.SPROUT:
              imagePath = '/img/farming/healthy_plant.png';
              break;
            case CROP_STAGES.GROWN:
              // Use crop image for fully grown crops
              if (tile.seedType) {
                const cropImagePath = getCropImagePath(tile.seedType);
                imagePath = `/img/${cropImagePath}`;
              } else {
                imagePath = '/img/farming/healthy_plant.png';
              }
              break;
            case CROP_STAGES.WITHERED:
              imagePath = '/img/farming/wilted_plant.png';
              break;
          }
          
          if (imagePath && imagesRef.current[imagePath] && imagesRef.current[imagePath].complete) {
            const img = imagesRef.current[imagePath];
            
            // For seeds, draw smaller and centered
            if (tile.crop.stage === CROP_STAGES.SEED) {
              const seedSize = gameSettings.tileSize * 0.4; // Smaller for seeds
              const seedX = tileX + (gameSettings.tileSize - seedSize) / 2;
              const seedY = tileY + (gameSettings.tileSize - seedSize) / 2;
              offscreenCtx.drawImage(img, seedX, seedY, seedSize, seedSize);
            } else {
              // For other stages, use normal crop size
              offscreenCtx.drawImage(img, cropX, cropY, cropSize, cropSize);
            }
          }
          
          // Draw progress bar for crops (only for growing crops)
          if (tile.crop.stage !== CROP_STAGES.GROWN && tile.crop.stage !== CROP_STAGES.WITHERED && tile.seedType && tile.plantedAt) {
            const seedData = ItemData[tile.seedType];
            const growthTime = seedData?.growTime || gameSettings.growthTimeHours;
            const growthTimeInMinutes = growthTime * 60; // Convert hours to minutes
            const minutesElapsed = currentGameTime - tile.plantedAt;
            const progress = Math.min(minutesElapsed / growthTimeInMinutes, 1);
            
            // Draw progress bar background
            const barWidth = gameSettings.tileSize * 0.8;
            const barHeight = 5; // Slightly taller for bigger tiles
            const barX = tileX + (gameSettings.tileSize - barWidth) / 2;
            const barY = tileY + gameSettings.tileSize - 10; // More space from bottom
            
            offscreenCtx.fillStyle = 'rgba(0, 0, 0, 0.3)';
            offscreenCtx.fillRect(barX, barY, barWidth, barHeight);
            
            // Draw progress bar fill - color indicates watering status
            const lastSafeTimeBar = tile.lastWateredAt || tile.plantedAt;
            const minutesSinceLastSafeBar = currentGameTime - lastSafeTimeBar;
            
            let barColor;
            if (minutesSinceLastSafeBar <= 1440) {
              barColor = '#4CAF50'; // Green - safe
            } else if (minutesSinceLastSafeBar <= 2160) {
              barColor = '#FF9800'; // Orange - needs water
            } else if (minutesSinceLastSafeBar <= 2880) {
              barColor = '#F44336'; // Red - danger
            } else {
              barColor = '#9E9E9E'; // Gray - withered
            }
            
            offscreenCtx.fillStyle = barColor;
            offscreenCtx.fillRect(barX, barY, barWidth * progress, barHeight);
            
            // Draw progress bar border
            offscreenCtx.strokeStyle = '#333';
            offscreenCtx.lineWidth = 2; // Thicker border for larger tiles
            offscreenCtx.strokeRect(barX, barY, barWidth, barHeight);
          }
          
          // Draw status indicator
          if (tile.crop.stage === CROP_STAGES.GROWN) {
            // Green checkmark for fully grown crops
            offscreenCtx.fillStyle = '#4CAF50';
            offscreenCtx.beginPath();
            offscreenCtx.arc(tileX + gameSettings.tileSize - 12, tileY + 12, 6, 0, 2 * Math.PI);
            offscreenCtx.fill();
            
            // Add white checkmark
            offscreenCtx.strokeStyle = '#FFFFFF';
            offscreenCtx.lineWidth = 2;
            offscreenCtx.beginPath();
            offscreenCtx.moveTo(tileX + gameSettings.tileSize - 15, tileY + 12);
            offscreenCtx.lineTo(tileX + gameSettings.tileSize - 12, tileY + 15);
            offscreenCtx.lineTo(tileX + gameSettings.tileSize - 8, tileY + 9);
            offscreenCtx.stroke();
          } else if (tile.crop.stage !== CROP_STAGES.WITHERED) {
            // Water status indicator for growing crops
            const lastSafeTime = tile.lastWateredAt || tile.plantedAt;
            const minutesSinceLastSafe = currentGameTime - lastSafeTime;
            
            let indicatorColor;
            let indicatorSize = 4; // Larger for bigger tiles
            
            if (minutesSinceLastSafe <= 1440) {
              // Safe period (0-24 hours)
              indicatorColor = tile.lastWateredAt ? '#4A90E2' : '#87CEEB'; // Blue if watered, light blue if recently planted
            } else if (minutesSinceLastSafe <= 2160) {
              // Needs water but not urgent (24-36 hours)
              indicatorColor = '#FF9800'; // Orange
            } else if (minutesSinceLastSafe <= 2880) {
              // Danger period (36-48 hours)
              indicatorColor = '#F44336'; // Red
              indicatorSize = 5; // Larger to show urgency
            }
            
            if (indicatorColor) {
              offscreenCtx.fillStyle = indicatorColor;
              offscreenCtx.beginPath();
              offscreenCtx.arc(tileX + gameSettings.tileSize - 12, tileY + 12, indicatorSize, 0, 2 * Math.PI);
              offscreenCtx.fill();
            }
          }
        }
      }
    }
    
    // Copy the completed offscreen canvas to the visible canvas in one operation
    // This eliminates flicker by avoiding clearing the visible canvas until the new frame is ready
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(offscreenCanvas, 0, 0);
  }, [farmGrid, gameSettings]);

  // Update crops based on game time changes (throttled)
  useEffect(() => {
    if (farmGrid.length > 0) {
      const timeoutId = setTimeout(() => {
        const { updatedGrid, hasChanges } = updateCropsBasedOnTime(farmGrid);
        if (hasChanges) {
          setFarmGrid(updatedGrid);
          saveFarmState(updatedGrid, harvestedCrops);
        }
      }, 2000); // Increased delay to reduce rapid changes
      
      return () => clearTimeout(timeoutId);
    }
  }, [worldState?.currentTimeInMinutes, worldState?.day, farmGrid, updateCropsBasedOnTime, harvestedCrops, saveFarmState]); // Update when game time changes

  // Draw grid when it changes or when images are loaded - Optimized for smooth interaction
  useEffect(() => {
    if (farmGrid.length > 0 && imagesLoaded) {
      // Use requestAnimationFrame for smooth drawing without throttling the useEffect
      const drawFrame = () => {
        drawGrid();
      };
      
      const frameId = requestAnimationFrame(drawFrame);
      return () => cancelAnimationFrame(frameId);
    }
  }, [farmGrid, drawGrid, imagesLoaded]);

  // End game
  const endGame = useCallback(() => {
    // Just go back to wilderness without showing results
    onGameEnd({
      outcome: 'closed',
      farmState: {
        grid: farmGrid,
        harvestedCrops,
        lastUpdated: getGameTimeInMinutes()
      }
    });
  }, [onGameEnd, farmGrid, harvestedCrops, getGameTimeInMinutes]);

  // Start farming - No delay, show tools immediately
  const startFarming = useCallback(() => {
    setPhase(FARMING_PHASES.PLAYING);
    setMessage('Welcome to your farm! Use the tools to till, plant, water, and harvest.');
  }, []);

  // Initialize game
  useEffect(() => {
    initializeGrid();
  }, [initializeGrid]);

  // Update crops after grid is initialized (one-time check)
  useEffect(() => {
    if (gridInitialized && !cropsUpdated) {
      setTimeout(() => {
        setFarmGrid(prevGrid => {
          if (prevGrid.length === 0) return prevGrid;
          const { updatedGrid, hasChanges } = updateCropsBasedOnTime(prevGrid);
          if (hasChanges) {
            saveFarmState(updatedGrid, harvestedCrops);
            return updatedGrid;
          }
          return prevGrid;
        });
        setCropsUpdated(true);
      }, 100); // Small delay to avoid initialization issues
    }
  }, [gridInitialized, cropsUpdated, updateCropsBasedOnTime, saveFarmState, harvestedCrops]); // Only run when grid is first loaded

  // Set up canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = gameSettings.tileSize * gameSettings.gridWidth;
      canvas.height = gameSettings.tileSize * gameSettings.gridHeight;
      canvas.addEventListener('click', handleCanvasClick);
      
      // Force initial draw after canvas is set up
      if (farmGrid.length > 0) {
        setTimeout(() => drawGrid(), 100);
      }
      
      return () => {
        canvas.removeEventListener('click', handleCanvasClick);
      };
    }
  }, [handleCanvasClick, gameSettings, farmGrid, drawGrid]);

  // Auto-save periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (farmGrid.length > 0) {
        saveFarmState(farmGrid, harvestedCrops);
      }
    }, 30000); // Save every 30 seconds
    
    return () => clearInterval(interval);
  }, [farmGrid, harvestedCrops, saveFarmState]);

  const renderWelcomeModal = () => {
    if (!showWelcome) return null;
    
    return (
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <h2>Welcome to Pixel Farm!</h2>
          <p>
            1. Select the <strong>Hoe</strong> (⛏️) to till the soil (5 energy).<br/>
            2. Select seeds from your inventory and use <strong>Plant</strong> (🌱) to plant them (consumes seed).<br/>
            3. Use the <strong>Watering Can</strong> (💧) to water your crops (5 energy).<br/>
            4. Use the <strong>Shovel</strong> (🪣) to dig up seeds and return them to inventory (5 energy).<br/>
            5. <strong>NEW Watering System:</strong> Seeds get 24 hours before needing water!<br/>
            6. After that, crops get 24 hours to find water before they wither.<br/>
            7. <strong>Visual Indicators:</strong> Blue = safe, Orange = needs water, Red = danger!<br/>
            8. <strong>Fully grown crops don't need watering</strong> - they just wait for harvest!<br/>
            9. Once grown, use the <strong>Scythe</strong> (🌾) to harvest (free)!<br/>
            10. You can leave and return anytime - your crops will keep growing!
          </p>
          <button 
            className={styles.modalButton}
            onClick={() => {
              setShowWelcome(false);
              startFarming();
            }}
          >
            Start Farming!
          </button>
        </div>
      </div>
    );
  };



  return (
    <div className={styles.farmingOverlay}>
      <div className={styles.farmingWindow}>
        <div className={styles.messageArea}>
          {message}
        </div>
        
        <div className={styles.gameContainer}>
          {/* Left sidebar with status and stats */}
          {(phase === FARMING_PHASES.PLAYING || phase === FARMING_PHASES.RESULT) && (
            <div className={styles.leftSidebar}>
              <div className={styles.statusSection}>
                <StatusDisplay 
                  health={playerData.health}
                  mana={playerData.mana}
                  energy={playerData.energy}
                />
                <div className={styles.farmingStats}>
                  <p>Harvested: <span>{harvestedCrops}</span></p>
                </div>
              </div>
              
              {/* Back button moved to left sidebar */}
              {phase === FARMING_PHASES.PLAYING && (
                <div className={styles.actionsPanel}>
                  <button 
                    className={styles.actionButton}
                    onClick={endGame}
                  >
                    Back to Wilderness
                  </button>
                </div>
              )}
            </div>
          )}
          
          <div className={styles.canvasContainer}>
            <canvas 
              ref={canvasRef}
              className={styles.farmCanvas}
            />
            
            {/* Selected seed display */}
            {phase === FARMING_PHASES.PLAYING && (
              <div className={styles.selectedSeedDisplay}>
                {selectedSeed && ItemData[selectedSeed] ? (
                  <div className={styles.selectedSeedInfo}>
                    <img 
                      src={`/img/${ItemData[selectedSeed].img}`} 
                      alt={ItemData[selectedSeed].name}
                      className={styles.selectedSeedIcon}
                    />
                    <div className={styles.selectedSeedDetails}>
                      <span className={styles.selectedSeedName}>{ItemData[selectedSeed].name}</span>
                      <span className={styles.selectedSeedGrowTime}>Grows in {ItemData[selectedSeed].growTime}h</span>
                    </div>
                  </div>
                ) : (
                  <div className={styles.noSeedSelected}>
                    <span>No seed selected - Choose a seed to plant</span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Right sidebar with tools and inventory */}
          {(phase === FARMING_PHASES.PLAYING || phase === FARMING_PHASES.RESULT) && (
            <div className={styles.rightSidebar}>
              <div className={styles.toolsPanel}>
                <h3>Tools</h3>
                <div className={styles.toolsGrid}>
                  <button 
                    className={`${styles.toolButton} ${currentTool === 'hoe' ? styles.selected : ''}`}
                    onClick={() => setCurrentTool('hoe')}
                  >
                    <img src={TOOL_ICONS.hoe} alt="Hoe" className={styles.toolIcon} />
                    <span>Hoe (5E)</span>
                  </button>
                  <button 
                    className={`${styles.toolButton} ${currentTool === 'watering-can' ? styles.selected : ''}`}
                    onClick={() => setCurrentTool('watering-can')}
                  >
                    <img src={TOOL_ICONS['watering-can']} alt="Watering Can" className={styles.toolIcon} />
                    <span>Water (5E)</span>
                  </button>
                  <button 
                    className={`${styles.toolButton} ${currentTool === 'scythe' ? styles.selected : ''}`}
                    onClick={() => setCurrentTool('scythe')}
                  >
                    <img src={TOOL_ICONS.scythe} alt="Scythe" className={styles.toolIcon} />
                    <span>Scythe</span>
                  </button>
                  <button 
                    className={`${styles.toolButton} ${currentTool === 'plant' ? styles.selected : ''}`}
                    onClick={() => setCurrentTool('plant')}
                  >
                    <img src={TOOL_ICONS.plant} alt="Plant" className={styles.toolIcon} />
                    <span>Plant</span>
                  </button>
                  <button 
                    className={`${styles.toolButton} ${currentTool === 'shovel' ? styles.selected : ''}`}
                    onClick={() => setCurrentTool('shovel')}
                  >
                    <img src={TOOL_ICONS.shovel} alt="Shovel" className={styles.toolIcon} />
                    <span>Shovel (5E)</span>
                  </button>
                </div>
              </div>

              {/* Seed Inventory Section - Compact like inventory display */}
              <div className={`${styles.seedsPanel} ${currentTool !== 'plant' ? styles.seedsPanelHidden : ''}`}>
                <h3>Select Seed</h3>
                {getAvailableSeeds().length > 0 ? (
                  <div className={styles.seedsGrid}>
                    {getAvailableSeeds().map(({ key, item, quantity }) => (
                      <div 
                        key={key}
                        className={`${styles.seedItem} ${selectedSeed === key ? styles.selected : ''}`}
                        onClick={() => setSelectedSeed(key)}
                        title={`${item.name} - ${item.growTime}h grow time`}
                      >
                        <img 
                          src={`/img/${item.img}`} 
                          alt={item.name}
                          className={styles.seedIcon}
                        />
                        {quantity > 1 && (
                          <span className={styles.seedQuantity}>{quantity}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={styles.noSeeds}>
                    No seeds available!<br/>
                    Find seeds by foraging.
                  </p>
                )}
              </div>

              {/* Collapsible Inventory Panel */}
              <div className={styles.inventoryPanelContainer}>
                <CollapsibleInventoryPanel 
                  playerData={playerData}
                  onUseItem={(itemKey) => {
                    // Handle using consumables in farming context
                    if (onEnergyUpdate) {
                      onEnergyUpdate({ 
                        consumableUse: itemKey 
                      });
                    }
                  }}
                  onEquipItem={null} // No equipment in farming
                  onSellItem={null} // No selling in farming
                  shopContext={false}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {renderWelcomeModal()}
    </div>
  );
};

export default FarmingMinigame;